import { useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { produce } from 'immer';
import { Button } from '@antimatterhq/ui/components/button';
import { Pre, Text } from '@antimatterhq/ui/components/typography';
import { Badge } from '@antimatterhq/ui/components/badge';
import { formatDate } from '@antimatterhq/ui/utils/format';
import { Skeleton } from '@antimatterhq/ui/components/skeleton';
import Spacer from '@antimatterhq/ui/components/spacer';
import { ScrollArea } from '@antimatterhq/ui/components/scrollArea';
import { destr } from 'destr';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { isAxiosError } from 'axios';
import { useDismissAlerts } from '@/hooks/dbui/useDismissAlerts.ts';
import {
  useGetAlertById,
  useOptimisticUpdateAlertById,
} from '@/hooks/dbui/useGetAlertById';
import { BackLink } from '@/components/link/back/BackLink';
import {
  setDismissAlertQueryData,
  useOptimisticUpdateAlertsInfinite,
} from '@/hooks/dbui/useGetAlertsInfinite';
import { getAlertBadgeColorVariant } from '@/lib/alerts/getAlertBadgeColorVariant';
import { getAlertStatusText } from '@/lib/alerts/getAlertStatusText';
import { capitalize } from '@/utils/capitalize';

const ViewHealthDetailsSkeleton = () => {
  return (
    <div>
      <BackLink fallbackRoute="/explore/health" />
      <Spacer size="base" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-96" /> {/* Title skeleton */}
          <Skeleton className="h-6 w-24" /> {/* Badge skeleton */}
        </div>
        <Button variant="primary" leftIcon="lucide:circle-x" disabled>
          Dismiss Alert
        </Button>
      </div>

      <div className="space-y-4">
        <div>
          <Skeleton className="inline-block h-4 w-48" />
          <Skeleton className="inline-block h-4 w-48" />
        </div>

        <div>
          <Skeleton className="inline-block h-4 w-48" />
          <Skeleton className="inline-block h-4 w-32" />
        </div>

        <div>
          <Skeleton className="inline-block h-4 w-48" />
          <Skeleton className="inline-block h-4 w-16" />
        </div>

        <div>
          <Skeleton className="inline-block h-4 w-48" />
          <Skeleton className="inline-block h-4 w-64" />
        </div>

        <div>
          <Skeleton className="inline-block h-4 w-48" />
          <Skeleton className="mt-2 h-24 w-full" />
        </div>
      </div>
    </div>
  );
};

const useListingQueryParamsFromUrl = () => {
  const includeNotOpen = useRef<boolean>(
    destr<boolean>(
      new URLSearchParams(window.location.search).get('includeNotOpen'),
    ) ?? true,
  );

  const date = useRef<Date | undefined>(
    destr<string | undefined>(
      new URLSearchParams(window.location.search).get('date'),
    )
      ? new Date(
          destr<string>(
            new URLSearchParams(window.location.search).get('date'),
          ),
        )
      : undefined,
  );
  const beforeDate = date && date.current && date.current.toISOString();

  return {
    includeNotOpen: includeNotOpen.current,
    beforeDate,
  };
};

export const ViewHealthDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: alert,
    isLoading,
    isFetching,
    isPending,
    error,
    refetch,
  } = useGetAlertById({ id: id! });

  const { mutateAsync: dismissAsync, isPending: isDismissing } =
    useDismissAlerts();
  const optimisticUpdate = useOptimisticUpdateAlertById();
  const optimisticUpdateAlertsInfinite = useOptimisticUpdateAlertsInfinite();

  const queryParams = useListingQueryParamsFromUrl();

  const handleDismiss = async () => {
    if (!alert?.id) return;

    await dismissAsync(
      { ids: [alert.id] },
      {
        toasts: {
          success: 'Alert dismissed successfully',
          error: 'Failed to dismiss alert',
        },
        onSuccess: async ({ variables }) => {
          const newData = produce(alert, (draft) => {
            draft.state = 'dismissed';
          });
          optimisticUpdate({ data: newData });
          optimisticUpdateAlertsInfinite(
            (prev) => setDismissAlertQueryData(prev, variables.ids!),
            { ...queryParams },
          );
        },
      },
    );
  };

  if (isLoading || isFetching || isPending) {
    return <ViewHealthDetailsSkeleton />;
  }

  if (error) {
    const errorMessage = isAxiosError(error)
      ? error.response?.data.message
      : (error.message ?? 'Please try again later.');

    const is404 = isAxiosError(error) && error.response?.status === 404;

    return (
      <GlobalError
        iconSize="sm"
        title={is404 ? 'Alert not found' : 'Unable to load alert'}
        retryText={is404 ? 'Return to list' : undefined}
        description={capitalize(errorMessage)}
        onRetry={async () => {
          if (is404) {
            navigate('/explore/health');
          } else {
            await refetch();
          }
        }}
      />
    );
  }

  if (!alert) {
    return (
      <GlobalError
        iconSize="sm"
        title="Alert not found"
        retryText="Return to list"
        onRetry={async () => {
          navigate('/explore/health');
        }}
      />
    );
  }

  return (
    <div className="max-h-full overflow-y-auto">
      <BackLink fallbackRoute="/explore/health" />
      <Spacer size="base" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Text size="2xl" className="font-bold">
            {alert.summary}
          </Text>
          <Badge
            variant="filled"
            color={getAlertBadgeColorVariant(alert.state)}
          >
            {getAlertStatusText(alert.state)}
          </Badge>
        </div>
        <Button
          variant="primary"
          leftIcon="lucide:circle-x"
          loading={isDismissing}
          disabled={alert?.state === 'exported' || alert?.state === 'dismissed'}
          onClick={() => handleDismiss()}
        >
          Dismiss Alert
        </Button>
      </div>

      <Spacer size="2xl" />

      <div className="space-y-4">
        <div>
          <Text className="font-bold">Event time: </Text>
          <Text>{formatDate(alert.eventTime)}</Text>
        </div>

        <div>
          <Text className="font-bold">Resource Type: </Text>
          <Text>{alert.resourceType}</Text>
        </div>

        <div>
          <Text className="font-bold">Duplicates: </Text>
          <Text>{alert.duplicates}</Text>
        </div>

        <div>
          <Text className="font-bold"> Resource Name: </Text>
          <Text>{alert.resourceName}</Text>
        </div>

        <div>
          <Text className="font-bold">Details:</Text>
          <ScrollArea orientation="horizontal">
            <Pre>{alert.details}</Pre>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
